<script setup lang="ts">
import { computed } from "vue";

import StringTooltip from "@/components/Tooltip/StringTooltip.vue";

import { isNumber } from "@/services/UtilitiesService";

import type { PagedData } from "@/models/PagedData";

const emit = defineEmits<(e: "doSearch", page: number, count: number) => void>();
withDefaults(defineProps<{
  result: PagedData<unknown[]>;
  count: number;
  page: number;
}>(), { count: 10,page: 1, })

const lastPage = computed((): number => {
  return Math.ceil(__props.result.totalCount / __props.count) || 1;
});

const isAtEnd = computed((): boolean => {
  return __props.result && __props.page >= lastPage.value;
});

const isAtStart = computed((): boolean => {
  return __props.page <= 1;
});

const startIndex = computed(() => {
  return (__props.result.page - 1) * __props.count;
});

const endIndex = computed(() => {
  return startIndex.value + Math.min(__props.result.items.length, __props.count);
});

function next() {
  if (__props.page * __props.count < __props.result.totalCount) {
    doSearch(__props.page + 1, __props.count);
  }
}

function prev() {
  if (__props.page > 1) {
    doSearch(__props.page - 1, __props.count);
  }
}

function paginate(newPage: number) {
  if (newPage > lastPage.value) doSearch(lastPage.value, __props.count);
  else if (newPage < 1) doSearch(1, __props.count);
  else doSearch(newPage, __props.count);
}

function doSearch(pageNumber: number, pageCount: number) {
  emit("doSearch", pageNumber, pageCount);
}
</script>
<template>
  <div class="tw-my-2 tw-flex tw-flex-wrap tw-items-center tw-justify-start tw-gap-2">
    <span
      role="text"
      tabindex="0"
      class="tw-min-w-max"
      :aria-label="`Count summary of items in Table. Showing items ${
        startIndex + Math.min(result.items.length, 1)
      } to ${endIndex} out of ${Math.max(result.totalCount, 0)} ${result.totalCount === 1 ? 'item' : 'items'}.`"
    >
      {{ startIndex + Math.min(result.items.length, 1) }}-{{ endIndex }} of
      {{ Math.max(result.totalCount, 0) }}
    </span>
    <div>
      <select
        id="pageCount"
        :value="count"
        class="tw-rounded-md tw-border tw-border-black/70 tw-p-1"
        title="Number of items per page"
        @change="doSearch(1, +($event.target as HTMLInputElement).value)"
      >
        <option>10</option>
        <option>15</option>
        <option>20</option>
        <option>25</option>
        <option>50</option>
        <option>75</option>
        <option>100</option>
      </select>
      <label for="pageCount"> items / page</label>
    </div>
    <div class="tw-shrink-0 tw-space-x-1">
      <StringTooltip item="Previous Page" :root="{ disabled: isAtStart }" #="{ disabled, item }">
        <button
          type="button"
          class="tw-aspect-square tw-size-6 tw-rounded-full tw-bg-white tw-p-1 tw-text-black"
          :aria-label="item"
          :disabled="disabled"
          :class="disabled ? 'tw-cursor-not-allowed tw-opacity-70' : 'hover:tw-brightness-75'"
          @click.prevent="prev"
        >
          <i :aria-hidden="true" class="fas fa-chevron-left" />
        </button>
      </StringTooltip>
      <input
        type="number"
        class="tw-w-8 tw-rounded-sm tw-border tw-px-1"
        aria-label="Page number. Press enter to view results in specified page."
        :value="page"
        :max="lastPage"
        @keypress="isNumber($event)"
        @keypress.enter="paginate(+($event.target as HTMLInputElement).value)"
      />
      / {{ lastPage }}
      <StringTooltip item="Next Page" :root="{ disabled: isAtEnd }" #="{ disabled, item }">
        <button
          type="button"
          class="tw-aspect-square tw-size-6 tw-rounded-full tw-bg-white tw-p-1 tw-text-black"
          :aria-label="item"
          :disabled="disabled"
          :class="disabled ? 'tw-cursor-not-allowed tw-opacity-70' : 'hover:tw-brightness-75'"
          @click.prevent="next"
        >
          <i :aria-hidden="true" class="fas fa-chevron-right" />
        </button>
      </StringTooltip>
    </div>
  </div>
</template>
